import { String } from 'effect'
import { NonEmptyArray } from 'helpers/array'

export const recordKeys = <K extends string, T>(object: Record<K, T>) =>
  Object.keys(object) as K[]

export const recordEntries = <K extends string, T>(object: Record<K, T>) =>
  Object.entries(object) as [K, T][]

export const recordFromEntries: <K extends string, T>(
  entries: [K, T][]
) => Record<K, T> = Object.fromEntries

export const removeKeysFromObject = <T extends Record<string, unknown>>(
  object: T,
  keysToRemove: Set<string>
) => {
  return Object.fromEntries(
    Object.entries(object).filter(([key]) => !keysToRemove.has(key.trim()))
  )
}

export const removeKeysFromObjects = <T extends Record<string, unknown>>(
  objects: T[],
  keysToRemove: string[]
) => {
  return objects.map((obj) => {
    return Object.fromEntries(
      Object.entries(obj).filter(
        ([key]) => !keysToRemove.map(String.trim).includes(key.trim())
      )
    )
  })
}

export const groupByT = <A, B>(
  as: A[],
  findKey: (a: A) => string,
  map: (a: A) => B
): Record<string, NonEmptyArray<B>> => {
  return as.reduce(
    (
      acc: Record<string, NonEmptyArray<B>>,
      a
    ): Record<string, NonEmptyArray<B>> => {
      const key = findKey(a)
      const value = map(a)
      const existing = acc[key]
      acc[key] = existing ? [...existing, value] : [value]
      return acc
    },
    {}
  )
}

export const groupByTM = <A, B>(
  as: A[],
  findKey: (a: A) => string,
  map: (a: A) => B,
  merge: NoInfer<(x: B, y: B) => B>
): Record<string, B> => {
  return as.reduce((acc: Record<string, B>, a): Record<string, B> => {
    const key = findKey(a)
    const value = map(a)
    const existing = acc[key]
    acc[key] = existing ? merge(existing, value) : value
    return acc
  }, {})
}

export const mapRecord = <A, B>(
  r: Record<string, A>,
  map: (k: string, a: A) => B
): Record<string, B> => {
  const o: Record<string, B> = {}
  for (const k in r) {
    if (r.hasOwnProperty(k) && r[k]) {
      o[k] = map(k, r[k])
    }
  }
  return o
}

type NestedObject = Record<string, any>

export function flattenObject(
  obj: NestedObject,
  parentKey = '',
  result: Record<string, string> = {}
): Record<string, string> {
  for (const key in obj) {
    const newKey = parentKey ? `${parentKey}.${key}` : key
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      flattenObject(obj[key], newKey, result)
    } else {
      result[newKey] = obj[key]
    }
  }
  return result
}
